import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { uploadDocument, getDocuments, approveDocument, downloadDocument, deleteDocument, getFrameworks, getTenantUsers, attachDocumentFile } from '@/services/grc';
import Pagination from '@/components/Pagination';
import { Upload, Download, CheckCircle, XCircle, FileText, Trash2, Paperclip, Users, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const DocumentsPage = () => {
  const [searchParams] = useSearchParams();
  const [documents, setDocuments] = useState([]);
  const [frameworks, setFrameworks] = useState([]);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [tenantTeams, setTenantTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [attachDialogOpen, setAttachDialogOpen] = useState(false);
  const [attachTarget, setAttachTarget] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [attaching, setAttaching] = useState(false);
  const fileInputRef = useRef(null);
  const attachInputRef = useRef(null);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    document_type: 'policy',
    framework_id: '',
    control_ids: [],
    owner: '',
    owner_type: 'user',
    approver: ''
  });

  const controlId = searchParams.get('control');

  useEffect(() => {
    loadFrameworks();
    loadTenantUsers();
  }, []);

  useEffect(() => {
    loadDocuments();
  }, [controlId, currentPage, pageSize]);

  const loadTenantUsers = async () => {
    try {
      const data = await getTenantUsers();
      setTenantUsers(data.users || []);
      setTenantTeams(data.teams || []);
    } catch (error) {
      console.error('Error loading tenant users:', error);
    }
  };

  const loadFrameworks = async () => {
    try {
      const data = await getFrameworks();
      setFrameworks(data.items || data);
    } catch (error) {
      console.error('Error loading frameworks:', error);
    }
  };

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, page_size: pageSize };
      if (controlId) params.control_id = controlId;
      const data = await getDocuments(params);
      if (data.items) {
        setDocuments(data.items);
        setTotalItems(data.total);
        setTotalPages(data.total_pages);
      } else {
        setDocuments(data);
        setTotalItems(data.length);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error loading documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handlePageSizeChange = (size) => { setPageSize(size); setCurrentPage(1); };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 25 * 1024 * 1024) {
        toast.error('File size exceeds 25MB limit');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) { toast.error('Please select a file'); return; }
    if (!formData.title || !formData.owner) { toast.error('Please fill in required fields'); return; }
    try {
      setUploading(true);
      const uploadFormData = new FormData();
      uploadFormData.append('file', selectedFile);
      uploadFormData.append('title', formData.title);
      uploadFormData.append('document_type', formData.document_type);
      uploadFormData.append('owner', formData.owner);
      if (formData.description) uploadFormData.append('description', formData.description);
      if (formData.framework_id) uploadFormData.append('framework_id', formData.framework_id);
      const controlIds = controlId ? [controlId] : [];
      uploadFormData.append('control_ids', JSON.stringify(controlIds));
      await uploadDocument(uploadFormData);
      toast.success('Document uploaded successfully');
      setUploadDialogOpen(false);
      resetForm();
      loadDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleAttachFile = async () => {
    if (!selectedFile || !attachTarget) return;
    try {
      setAttaching(true);
      const fd = new FormData();
      fd.append('file', selectedFile);
      await attachDocumentFile(attachTarget.id, fd);
      toast.success('File attached successfully');
      setAttachDialogOpen(false);
      setAttachTarget(null);
      setSelectedFile(null);
      if (attachInputRef.current) attachInputRef.current.value = '';
      loadDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to attach file');
    } finally {
      setAttaching(false);
    }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', document_type: 'policy', framework_id: '', control_ids: [], owner: '' });
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleApprove = async (documentId, action) => {
    try {
      await approveDocument(documentId, action, '');
      toast.success(`Document ${action}`);
      loadDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} document`);
    }
  };

  const handleDownload = async (doc) => {
    try {
      await downloadDocument(doc.id, doc.file_name);
      toast.success('Download started');
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      await deleteDocument(documentId);
      toast.success('Document deleted');
      loadDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const getApprovalBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return <Badge className={colors[status] || 'bg-gray-100 text-gray-800'} data-testid={`doc-status-${status}`}>{(status || 'draft').toUpperCase()}</Badge>;
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return null;
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const hasFile = (doc) => !!doc.file_name && !!doc.file_path;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" data-testid="documents-page-title">Document Management</h1>
          <p className="text-gray-600 mt-1">Upload and manage policy documents</p>
        </div>
        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="upload-document-btn">
              <Upload className="w-4 h-4 mr-2" />
              Upload Document
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Upload Document</DialogTitle>
              <DialogDescription>Upload policy documents (PDF, DOCX, XLSX - Max 25MB)</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label htmlFor="file">File *</Label>
                <div className="mt-2 flex items-center gap-2">
                  <Input ref={fileInputRef} id="file" type="file" accept=".pdf,.docx,.xlsx,.doc,.xls" onChange={handleFileSelect} className="flex-1" />
                  {selectedFile && <Badge variant="outline">{formatFileSize(selectedFile.size) || '0 Bytes'}</Badge>}
                </div>
              </div>
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input id="title" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="Document title" className="mt-2" />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Brief description" className="mt-2" rows={3} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="type">Document Type</Label>
                  <Select value={formData.document_type} onValueChange={(value) => setFormData({ ...formData, document_type: value })}>
                    <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="policy">Policy</SelectItem>
                      <SelectItem value="procedure">Procedure</SelectItem>
                      <SelectItem value="guideline">Guideline</SelectItem>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="owner">Owner * (Team or User)</Label>
                  <Select value={formData.owner} onValueChange={(value) => setFormData({ ...formData, owner: value })}>
                    <SelectTrigger className="mt-2"><SelectValue placeholder="Select owner" /></SelectTrigger>
                    <SelectContent>
                      {tenantTeams.length > 0 && (
                        <>
                          <SelectItem value="divider-teams" disabled className="font-semibold text-gray-500">— Teams —</SelectItem>
                          {tenantTeams.map(team => (
                            <SelectItem key={`team-${team.id}`} value={`team:${team.name}`}>
                              <div className="flex items-center gap-2"><Users className="w-3 h-3" />{team.name}</div>
                            </SelectItem>
                          ))}
                        </>
                      )}
                      {tenantUsers.length > 0 && (
                        <>
                          <SelectItem value="divider-users" disabled className="font-semibold text-gray-500">— Users —</SelectItem>
                          {tenantUsers.map(user => (
                            <SelectItem key={`user-${user.id}`} value={user.full_name || user.email}>{user.full_name || user.email}</SelectItem>
                          ))}
                        </>
                      )}
                      {tenantUsers.length === 0 && tenantTeams.length === 0 && (
                        <SelectItem value="none" disabled>No users available</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="approver">Approver</Label>
                  <Select value={formData.approver} onValueChange={(value) => setFormData({ ...formData, approver: value })}>
                    <SelectTrigger className="mt-2"><SelectValue placeholder="Select approver" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None (Auto-approve)</SelectItem>
                      {tenantUsers.map(user => (
                        <SelectItem key={user.id} value={user.id}>{user.full_name || user.email} ({user.role})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="framework">Framework (Optional)</Label>
                  <Select value={formData.framework_id} onValueChange={(value) => setFormData({ ...formData, framework_id: value })}>
                    <SelectTrigger className="mt-2"><SelectValue placeholder="Select framework" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {frameworks.map(fw => (
                        <SelectItem key={fw.id} value={fw.id}>{fw.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleUpload} disabled={uploading}>{uploading ? 'Uploading...' : 'Upload'}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Attach File Dialog */}
      <Dialog open={attachDialogOpen} onOpenChange={(open) => { setAttachDialogOpen(open); if (!open) { setAttachTarget(null); setSelectedFile(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Attach File</DialogTitle>
            <DialogDescription>
              Attach a file to: <strong>{attachTarget?.title}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Input ref={attachInputRef} type="file" accept=".pdf,.docx,.xlsx,.doc,.xls" onChange={handleFileSelect} data-testid="attach-file-input" />
            {selectedFile && <p className="text-sm text-gray-500">{selectedFile.name} ({formatFileSize(selectedFile.size)})</p>}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAttachDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleAttachFile} disabled={attaching || !selectedFile} data-testid="attach-file-submit">
                {attaching ? 'Attaching...' : 'Attach'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Framework</TableHead>
                <TableHead>Related Controls</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Uploaded</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8"><div className="flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div></TableCell></TableRow>
              ) : documents.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-500">No documents found</TableCell></TableRow>
              ) : (
                documents.map((doc) => (
                  <TableRow key={doc.id} data-testid={`doc-row-${doc.id}`}>
                    <TableCell>
                      <div className="font-medium">{doc.title}</div>
                      {hasFile(doc) ? (
                        <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                          <FileText className="w-3 h-3" />
                          <span>{doc.file_name}</span>
                          {formatFileSize(doc.file_size) && <span>({formatFileSize(doc.file_size)})</span>}
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-xs text-amber-600 mt-1">
                          <AlertTriangle className="w-3 h-3" />
                          <span>No file attached</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{doc.document_type}</Badge>
                    </TableCell>
                    <TableCell>
                      {(doc.framework_name || doc.framework) ? (
                        <Badge className="bg-blue-100 text-blue-800">{doc.framework_code || doc.framework_name || doc.framework}</Badge>
                      ) : (
                        <span className="text-gray-400 text-sm">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {doc.control_names && doc.control_names.length > 0 ? (
                        <div className="max-w-xs">
                          {doc.control_names.slice(0, 2).map((ctrl, idx) => (
                            <div key={idx} className="text-xs text-gray-600 truncate" title={ctrl}>{ctrl}</div>
                          ))}
                          {doc.control_names.length > 2 && <span className="text-xs text-gray-400">+{doc.control_names.length - 2} more</span>}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">—</span>
                      )}
                    </TableCell>
                    <TableCell>{doc.owner || <span className="text-gray-400">—</span>}</TableCell>
                    <TableCell>{getApprovalBadge(doc.approval_status || doc.status || 'draft')}</TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        {!hasFile(doc) && (
                          <Button size="sm" variant="ghost" className="text-blue-600" data-testid={`attach-btn-${doc.id}`}
                            onClick={() => { setAttachTarget(doc); setSelectedFile(null); setAttachDialogOpen(true); }}>
                            <Paperclip className="w-4 h-4" />
                          </Button>
                        )}
                        {hasFile(doc) && (
                          <Button size="sm" variant="ghost" onClick={() => handleDownload(doc)} data-testid={`download-btn-${doc.id}`}>
                            <Download className="w-4 h-4" />
                          </Button>
                        )}
                        {(doc.approval_status === 'pending' || (!doc.approval_status && doc.status !== 'approved')) && (
                          <>
                            <Button size="sm" variant="ghost" className="text-green-600" data-testid={`approve-btn-${doc.id}`} onClick={() => handleApprove(doc.id, 'approved')}>
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-600" data-testid={`reject-btn-${doc.id}`} onClick={() => handleApprove(doc.id, 'rejected')}>
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDelete(doc.id)} data-testid={`delete-btn-${doc.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} pageSize={pageSize} onPageChange={handlePageChange} onPageSizeChange={handlePageSizeChange} />
    </div>
  );
};

export default DocumentsPage;
